import type { Permissions } from "@krut/shared";
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { relative } from "../lib/cron.ts";
import { api, unwrap } from "../api.ts";
import { useMe } from "../hooks.ts";
import { Badge, Button, Card, cx, ErrorText, PageHeader, Segmented, Spinner } from "../components/ui.tsx";
import { EventBadge, fmtUsd, PhaseBadge, UsageBar } from "../components/runtime.tsx";
import { MyTimeSaved } from "../components/TimeSaved.tsx";
import { BarList, ColumnChart, compact } from "../components/charts.tsx";
import { Icon, type IconName } from "../components/icons.tsx";

export function HomePage() {
  const me = useMe();
  const qc = useQueryClient();
  const rt = useQuery({ queryKey: ["my-runtime"], queryFn: () => unwrap(api.me.runtime.$get()), refetchInterval: 8_000 });
  const usage = useQuery({ queryKey: ["my-usage"], queryFn: () => unwrap(api.me.usage.$get()) });
  const chats = useQuery({ queryKey: ["dash-chats"], queryFn: () => unwrap(api.me.assistant.sessions.$get({ query: { limit: "40", offset: "0" } })) });
  const jobs = useQuery({ queryKey: ["jobs"], queryFn: () => unwrap(api.me.assistant.jobs.$get()) });
  const runs = useQuery({ queryKey: ["recent-runs"], queryFn: () => unwrap(api.me.assistant.runs.$get({ query: { limit: "20" } })), retry: false });
  const restart = useMutation({
    mutationFn: () => unwrap(api.me.runtime.restart.$post()),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-runtime"] }),
  });
  if (!me.data) return null;
  const { user, group } = me.data;
  const r = rt.data?.runtime;
  const budget = group?.quotas.monthlyBudgetUsd ?? null;
  const daily = usage.data?.daily ?? [];
  const requests30 = daily.reduce((a, d) => a + d.requests, 0);
  const weekAgo = Date.now() / 1000 - 7 * 86400;
  const chatsThisWeek = (chats.data?.sessions ?? []).filter((c) => c.lastActive >= weekAgo).length;
  const jobList = (jobs.data?.jobs ?? []) as { enabled?: boolean; state?: string }[];
  const activeJobs = jobList.filter((j) => j.enabled !== false && j.state !== "paused").length;
  const runList = runs.data?.runs ?? [];
  const failed = runList.filter((x) => x.status === "failed").length;

  return (
    <>
      <PageHeader title="Dashboard" description={`Welcome back, ${user.name.split(" ")[0]}. Here's what your assistant has been up to.`} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="AI spend this month" value={fmtUsd(r?.spendUsd ?? 0)} sub={budget === null ? "No limit" : `of ${fmtUsd(budget)} budget`} />
        <Tile label="AI requests, last 30 days" value={requests30.toLocaleString()} />
        <Tile label="Chats active this week" value={chats.data ? `${chatsThisWeek}${chats.data.hasMore && chatsThisWeek === chats.data.sessions.length ? "+" : ""}` : "—"} to="/chat" />
        <Tile label="Scheduled tasks" value={String(activeJobs)} sub={runList.length ? `${runList.length - failed} of ${runList.length} recent runs succeeded` : "No runs yet"} to="/schedules/tasks" />
      </div>

      <MyTimeSaved />

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <DailyChart daily={daily} />
        </Card>
        <Card className="p-5">
          <h2 className="text-sm font-semibold">Models used</h2>
          <p className="mb-5 text-xs text-[var(--muted)]">Requests per model this month</p>
          <ModelBars rows={usage.data?.byModel ?? []} />
        </Card>
      </div>

      <Card className="mt-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-base font-semibold">Your assistant</h2>
              {r && <PhaseBadge phase={r.phase} />}
            </div>
            <p className="mt-1 text-sm text-[var(--muted)]">
              {rt.isPending ? "" : !rt.data?.enabled ? "Personal assistants are not enabled yet." : !r ? "Being created — this takes a minute or two the first time." : r.phase === "stopped" ? "An admin stopped it. Start it to chat, use your files and run scheduled tasks." : r.message ?? (r.phase === "running" ? "Ready." : "")}
            </p>
          </div>
          {rt.data?.enabled && (
            <Button disabled={restart.isPending} onClick={() => restart.mutate()}>
              {restart.isPending ? <Spinner /> : null}
              {!r ? "Create now" : r.phase === "stopped" ? "Start" : "Restart"}
            </Button>
          )}
        </div>
        <ErrorText error={restart.error} />
        {r && (
          <div className="mt-6 grid gap-6 sm:grid-cols-3">
            <div>
              <div className="mb-2 text-xs font-medium text-[var(--muted)]">Storage</div>
              <UsageBar used={r.storageUsedBytes} total={r.storageCapacityBytes} />
            </div>
            <div>
              <div className="mb-2 text-xs font-medium text-[var(--muted)]">AI budget this month</div>
              <UsageBar used={r.spendUsd} total={budget} label={budget === null ? `${fmtUsd(r.spendUsd)} · no limit` : `${fmtUsd(r.spendUsd)} of ${fmtUsd(budget)}`} />
            </div>
            <div>
              <div className="mb-2 text-xs font-medium text-[var(--muted)]">Plan</div>
              <div className="flex flex-wrap gap-1.5">
                <Badge tone="accent">{group?.name ?? "No group"}</Badge>
                {r.quotas && <Badge>{r.quotas.memoryMb} MB memory</Badge>}
              </div>
            </div>
          </div>
        )}
      </Card>

      <Activity
        chats={chats.data?.sessions ?? []}
        runs={runList}
        events={rt.data?.events ?? []}
      />

      {group && <Capabilities permissions={group.permissions} />}
    </>
  );
}

function Tile({ label, value, sub, to }: { label: string; value: string; sub?: string; to?: string }) {
  const body = (
    <Card className={cx("h-full p-4", to && "transition-shadow hover:shadow-[0_0_0_1px_var(--line-strong),var(--shadow)]")}>
      <div className="flex items-center justify-between gap-2 text-[12.5px] text-[var(--muted)]">
        {label}
        {to && <Icon name="arrowRight" size={14} className="shrink-0 text-[var(--muted)]/60" />}
      </div>
      <div className="mt-2 text-[26px] leading-none font-semibold tracking-[-0.02em]">{value}</div>
      {sub && <div className="mt-1.5 text-xs text-[var(--muted)]">{sub}</div>}
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

/** One measure at a time (never two scales on one chart): requests or spend per day, last 30 days. */
function DailyChart({ daily }: { daily: { day: string; spendUsd: number; requests: number }[] }) {
  const [measure, setMeasure] = useState<"requests" | "spendUsd">("requests");
  const days = useMemo(() => {
    const by = new Map(daily.map((d) => [d.day, d]));
    return Array.from({ length: 30 }, (_, i) => {
      const d = new Date(Date.now() - (29 - i) * 86400_000);
      const key = d.toISOString().slice(0, 10);
      return { key, label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }), requests: by.get(key)?.requests ?? 0, spendUsd: by.get(key)?.spendUsd ?? 0 };
    });
  }, [daily]);
  const fmt = (v: number) => (measure === "requests" ? v.toLocaleString() : fmtUsd(v));
  const total = days.reduce((a, d) => a + d[measure], 0);
  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">AI use</h2>
          <p className="text-xs text-[var(--muted)]">Last 30 days</p>
          <div className="mt-3 text-[22px] leading-none font-semibold tracking-[-0.02em]">
            {fmt(total)}
            <span className="ml-1.5 text-[13px] font-normal tracking-normal text-[var(--muted)]">{measure === "requests" ? "requests" : "spent"}</span>
          </div>
        </div>
        <Segmented label="Measure" value={measure} onChange={setMeasure} options={[{ value: "requests", label: "Requests" }, { value: "spendUsd", label: "Spend" }]} />
      </div>
      <ColumnChart
        label={`AI ${measure === "requests" ? "requests" : "spend"} per day, last 30 days`}
        data={days.map((d) => ({ key: d.key, label: d.label, value: d[measure] }))}
        format={(v) => (measure === "requests" ? `${v.toLocaleString()} requests` : fmtUsd(v))}
        tick={(v) => (measure === "spendUsd" ? `$${compact(v)}` : compact(v))}
        xLabels={[days[0]!.label, days[15]!.label, "Today"]}
      />
    </div>
  );
}

function ModelBars({ rows }: { rows: { model: string; requests: number; spendUsd: number }[] }) {
  if (!rows.length) return <p className="text-sm text-[var(--muted)]">No AI use yet this month.</p>;
  return <BarList rows={rows.map((r) => ({ key: r.model, label: r.model.split("/").pop() ?? r.model, value: r.requests, note: r.requests.toLocaleString(), title: `${r.requests.toLocaleString()} requests · ${fmtUsd(r.spendUsd)}` }))} />;
}

type ActivityItem = { at: number; key: string; icon: IconName; text: ReactNode; to?: string; badge?: ReactNode };

/** Chats, scheduled-task runs and assistant events, newest first. */
function Activity({
  chats,
  runs,
  events,
}: {
  chats: { id: string; title: string; source: string; lastActive: number }[];
  runs: { id: string; jobName: string; trigger: string; status: string; startedAt: string | null; error: string | null }[];
  events: { id: number; at: string; type: string; message: string }[];
}) {
  const items: ActivityItem[] = [
    ...chats.filter((c) => c.source !== "cron").slice(0, 8).map((c) => ({ at: c.lastActive * 1000, key: `c${c.id}`, icon: "chat" as const, text: c.title, to: `/chat/${c.id}` })),
    ...runs.slice(0, 8).map((r) => ({
      at: r.startedAt ? new Date(r.startedAt).getTime() : 0,
      key: `r${r.id}`,
      icon: "clock" as const,
      text: <>{r.jobName || "Scheduled task"} <span className="text-[var(--muted)]">· {r.trigger === "manual" ? "run manually" : "scheduled run"}</span></>,
      to: "/schedules/tasks",
      badge: <Badge tone={r.status === "completed" ? "good" : r.status === "failed" ? "bad" : "accent"}>{r.status === "completed" ? "Done" : r.status === "failed" ? "Failed" : "Running"}</Badge>,
    })),
    ...events.slice(0, 8).map((e) => ({ at: new Date(e.at).getTime(), key: `e${e.id}`, icon: "settings" as const, text: e.message, badge: <EventBadge type={e.type} /> })),
  ]
    .sort((a, b) => b.at - a.at)
    .slice(0, 12);
  return (
    <>
      <h2 className="mt-10 mb-3 text-base font-semibold">Recent activity</h2>
      <Card className="divide-y divide-[var(--line)]">
        {!items.length && <div className="px-5 py-6 text-sm text-[var(--muted)]">Nothing yet — start a chat or schedule a task.</div>}
        {items.map((i) => {
          const row = (
            <div className="flex items-center gap-3 px-5 py-3 text-sm">
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[var(--panel-2)] text-[var(--muted)]"><Icon name={i.icon} size={15} /></span>
              <span className="min-w-0 flex-1 truncate">{i.text}</span>
              {i.badge}
              <span className="w-28 shrink-0 text-right text-xs text-[var(--muted)]" title={new Date(i.at).toLocaleString()}>{relative(new Date(i.at).toISOString())}</span>
            </div>
          );
          return i.to ? <Link key={i.key} to={i.to} className="block hover:bg-[var(--panel-2)]">{row}</Link> : <div key={i.key}>{row}</div>;
        })}
      </Card>
    </>
  );
}

const CAPS: { key: "terminal" | "browser" | "webTools" | "cronJobs" | "subagents"; label: string }[] = [
  { key: "webTools", label: "Search and read the web" },
  { key: "terminal", label: "Run commands and code in your workspace" },
  { key: "browser", label: "Use a web browser" },
  { key: "cronJobs", label: "Schedule recurring tasks" },
  { key: "subagents", label: "Split work across helper agents" },
];

function Capabilities({ permissions }: { permissions: Permissions }) {
  return (
    <>
      <h2 className="mt-10 mb-3 text-base font-semibold">What your assistant can do</h2>
      <Card className="grid gap-x-8 gap-y-2.5 p-5 sm:grid-cols-2">
        {CAPS.map((c) => {
          const on = permissions[c.key];
          return (
            <div key={c.key} className={on ? "flex items-center gap-2.5 text-sm" : "flex items-center gap-2.5 text-sm text-[var(--muted)] line-through decoration-[var(--line)]"}>
              <span className={on ? "grid h-5 w-5 place-items-center rounded-full bg-emerald-500/15 text-emerald-700" : "grid h-5 w-5 place-items-center rounded-full bg-[var(--panel-2)]"}><Icon name={on ? "check" : "x"} size={12} /></span>
              {c.label}
            </div>
          );
        })}
      </Card>
      <p className="mt-2 text-xs text-[var(--muted)]">Set by your group. Ask an admin if you need something that's switched off.</p>
    </>
  );
}
